import { createContext, useContext, useState, type ReactNode } from "react";
import { ChevronsLeft, ChevronsRight, Download, Loader2, type LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { useToast } from "../../context/ToastContext.js";
import { Button } from "../ui/button.js";
import { Badge } from "../ui/badge.js";
import { Card } from "../ui/card.js";
import { Skeleton } from "../ui/skeleton.js";
import { Pagination, type PaginationMeta } from "../ui/pagination.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs.js";
import { SpecularButton } from "../ui/specular-button.js";
import { useHorizontalScrollEdges } from "../../hooks/useHorizontalScrollEdges.js";
import { DropdownMultiSelect, DropdownSelect } from "../DropdownButton.js";
import { SummaryStatCard, type SummaryStatCardProps } from "../SummaryStatCard.js";
import type { OrgMeta } from "./useReport.js";

// The building blocks the four HR report pages share: page frame, filter bar,
// export button, card grid, ranked list card, table frame and footer.

export function ReportPage({ title, description, children }: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5 px-8 pt-6 pb-10">
      <div>
        <h1 className="text-[22px] font-bold text-surface-900">{title}</h1>
        <p className="mt-0.5 text-[13px] text-surface-500">{description}</p>
      </div>
      {children}
    </div>
  );
}

/** The row between the filters and the table: optional content on the left (a legend),
 *  the report's actions (Export) on the right. */
export function TableToolbar({ left, children }: { left?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">{left}</div>
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </div>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return <div className="rounded-md bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">{message}</div>;
}

/** A page keeps one "which dropdown is open" state; this is its shape. */
export interface OpenState {
  open: string | null;
  setOpen: (fn: (cur: string | null) => string | null) => void;
}

export function dropdownProps(key: string, s: OpenState) {
  return {
    open: s.open === key,
    onToggle: () => s.setOpen((cur) => (cur === key ? null : key)),
    onClose: () => s.setOpen((cur) => (cur === key ? null : cur)),
  };
}

/** A multi-select filter's URL value: ids joined by commas, "" for "all". */
export const splitList = (v: string): string[] => (v ? v.split(",").filter(Boolean) : []);
export const joinList = (v: string[]): string => v.join(",");

/** Office + Department (multi-select), departments narrowed to the chosen offices. */
export function OrgFilters({ meta, officeIds, departmentIds, onChange, openState }: {
  meta: OrgMeta;
  officeIds: string;
  departmentIds: string;
  onChange: (patch: { officeIds?: string; departmentIds?: string }) => void;
  openState: OpenState;
}) {
  const offices = splitList(officeIds);
  const departments = meta.departments.filter((d) => offices.length === 0 || (d.office && offices.includes(d.office.id)));
  return (
    <>
      <DropdownMultiSelect
        values={offices}
        onChange={(v) => {
          // Keep only the picked departments that still belong to a picked office.
          const allowed = new Set(meta.departments.filter((d) => v.length === 0 || (d.office && v.includes(d.office.id))).map((d) => d.id));
          onChange({ officeIds: joinList(v), departmentIds: joinList(splitList(departmentIds).filter((id) => allowed.has(id))) });
        }}
        allLabel="All Offices"
        ariaLabel="Office"
        options={meta.offices.map((o) => ({ value: o.id, label: o.name }))}
        {...dropdownProps("office", openState)}
      />
      <DropdownMultiSelect
        values={splitList(departmentIds)}
        onChange={(v) => onChange({ departmentIds: joinList(v) })}
        allLabel="All Departments"
        ariaLabel="Department"
        options={departments.map((d) => ({ value: d.id, label: d.name, sub: d.office?.name }))}
        {...dropdownProps("dept", openState)}
      />
    </>
  );
}

export const STATUS_GROUP_OPTIONS = [
  { value: "current", label: "Current employees" },
  { value: "exited", label: "Exited employees" },
  { value: "all", label: "All employees" },
];

/** A GET download, so a plain Button with a busy state rather than AsyncButton. */
export function ExportButton({ onExport, label = "Export Excel" }: { onExport: () => Promise<void>; label?: string }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        onExport()
          .catch((e: unknown) => toast.show(e instanceof Error ? e.message : "Export failed", "error"))
          .finally(() => setBusy(false));
      }}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {label}
    </Button>
  );
}

/** The summary tiles: compact bars that wrap, the same look as the Expense Report KPIs. */
export function CardGrid({ children }: { children: ReactNode }) {
  // A grid, not flex-wrap, so a tile alone on the last row keeps the others’ width.
  return <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2.5">{children}</div>;
}

/** A report summary tile: SummaryStatCard as a compact bar sized for CardGrid. */
export function ReportStatCard({ className, ...props }: SummaryStatCardProps) {
  return <SummaryStatCard layout="row" wrapLabel className={cn("min-w-0", className)} {...props} />;
}

export interface RankedItem {
  key: string;
  primary: string;
  secondary?: string;
  value: string;
}

/** Rank, name, code and a value on the right -- the body of a "Top 5" card. */
function RankedList({ items, empty }: { items: RankedItem[]; empty: string }) {
  if (items.length === 0) return <p className="py-3 text-[12px] text-surface-500">{empty}</p>;
  return (
    <ol className="flex flex-col gap-1.5">
      {items.map((it, i) => (
        <li key={it.key} className="flex items-center gap-2 text-[13px]">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">{i + 1}</span>
          <span className="min-w-0 flex-1 truncate">
            <span className="font-semibold text-surface-900">{it.primary}</span>
            {it.secondary && <span className="ml-1 text-[11px] text-surface-500">{it.secondary}</span>}
          </span>
          <span className="shrink-0 font-semibold tabular-nums text-surface-900">{it.value}</span>
        </li>
      ))}
    </ol>
  );
}

/** A "Top 5" style list card: rank, name, code and a value on the right. */
export function TopListCard({ icon: Icon, title, items, empty, className }: {
  icon: LucideIcon;
  title: string;
  items: RankedItem[];
  empty: string;
  className?: string;
}) {
  return (
    <Card className={cn("p-4", className)}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-700">
        <Icon size={14} className="text-brand-600" /> {title}
      </div>
      <RankedList items={items} empty={empty} />
    </Card>
  );
}

export interface TopListTab {
  value: string;
  label: string;
  items: RankedItem[];
  empty: string;
}

/** Several full ranked lists, one per tab, scrolling inside the card. */
export function TopListTabsCard({ icon: Icon, title, tabs, className }: {
  icon: LucideIcon;
  title: string;
  tabs: TopListTab[];
  className?: string;
}) {
  return (
    <Card className={cn("flex max-h-80 flex-col p-4", className)}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-700">
        <Icon size={14} className="text-brand-600" /> {title}
      </div>
      <Tabs defaultValue={tabs[0]?.value ?? ""} className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mt-1 shrink-0">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="px-2 py-1.5 text-[12px]">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
            <RankedList items={t.items} empty={t.empty} />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}

/** Sticky identity columns (Name, then Code) for wide report tables. The Code column's
 *  offset is the Name column's inner width (180px) plus its px-3 padding. */
export const stickyName = "sticky left-0 z-10 px-3";
export const stickyCode = "sticky left-[204px] z-10 px-3";
export const NAME_WIDTH = "w-[180px]";
export const CODE_WIDTH = "w-[112px]";

const ScrollEdges = createContext<{ canScrollLeft: boolean; scrollToStart: () => void } | null>(null);

/** The scroll-back-to-start control, as on the Performance reviews table. Goes in the
 *  first (sticky) header cell, before its label; shows only once the table is scrolled. */
export function TableScrollStart() {
  const edges = useContext(ScrollEdges);
  if (!edges?.canScrollLeft) return null;
  return (
    <SpecularButton onClick={edges.scrollToStart} aria-label="Scroll left back to the start" size="sm" className="mr-1.5 w-6 px-0 align-middle">
      <ChevronsLeft size={13} />
    </SpecularButton>
  );
}

/**
 * The scroll frame every report table sits in, with loading and empty states. While
 * columns are hidden off to the right, a scroll-right control is pinned to the right
 * edge of the header. `contentKey` should change whenever the columns do, so the
 * frame re-measures.
 */
export function ReportTableFrame({ loading, empty, emptyText, contentKey, children }: {
  loading: boolean;
  empty: boolean;
  emptyText: string;
  contentKey?: string;
  children: ReactNode;
}) {
  const { ref, canScrollLeft, canScrollRight, scrollToStart, scrollToEnd } = useHorizontalScrollEdges([loading, empty, contentKey]);
  return (
    <div className="relative">
      <div ref={ref} className="overflow-x-auto rounded-md border border-surface-200">
        {loading && empty ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true" aria-label="Loading report">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}
          </div>
        ) : empty ? (
          <div className="p-10 text-center text-[13px] text-surface-500">{emptyText}</div>
        ) : (
          <ScrollEdges.Provider value={{ canScrollLeft, scrollToStart }}>
            <div className={cn("transition-opacity", loading && "opacity-60")}>{children}</div>
          </ScrollEdges.Provider>
        )}
      </div>
      {canScrollRight && !empty && (
        <div className="absolute top-px right-px z-20 rounded-tr-md bg-surface-50 py-1.5 pr-2 pl-3 shadow-[-8px_0_8px_-4px_rgba(0,0,0,0.08)]">
          <SpecularButton onClick={scrollToEnd} aria-label="Scroll right to see more columns" size="sm" className="w-7 px-0">
            <ChevronsRight size={14} />
          </SpecularButton>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZES = ["25", "50", "100"].map((v) => ({ value: v, label: `${v} / page` }));

/** Rows-per-page picker; sits in the TableToolbar above the table, next to Export. */
export function PageSizeSelect({ limit, onLimit, openState }: {
  limit: string;
  onLimit: (limit: string) => void;
  openState: OpenState;
}) {
  return (
    <DropdownSelect
      value={limit}
      onChange={onLimit}
      options={PAGE_SIZES}
      placeholder="Rows per page"
      ariaLabel="Rows per page"
      clearable={false}
      {...dropdownProps("pageSize", openState)}
    />
  );
}

export function ReportFooter({ meta, onPage }: {
  meta: PaginationMeta | undefined;
  onPage: (page: number) => void;
}) {
  if (!meta || meta.total === 0) return null;
  return <Pagination meta={meta} onPageChange={onPage} className="mt-0" />;
}

export type YesNoState = "Y" | "N" | "E";
const YN_TONE = { Y: "success", N: "error", E: "warning" } as const;
const YN_TEXT = { Y: "Yes", N: "No", E: "Expired" } as const;

/** The green Yes / red No / amber Expired pill of the document report. */
export function YesNoBadge({ state, title, children }: { state: YesNoState; title?: string | undefined; children?: ReactNode }) {
  return (
    <Badge tone={YN_TONE[state]} title={title} className="justify-center">
      {children ?? YN_TEXT[state]}
    </Badge>
  );
}
